import React, { useMemo, useState } from 'react';
import {
  Paper,
  Typography,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Divider,
  Alert,
  Box,
  Button,
  Slider,
  TextField,
  InputAdornment,
  Switch,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ImageIcon from '@mui/icons-material/Image';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import CategoryIcon from '@mui/icons-material/Category';
import StoreIcon from '@mui/icons-material/Store';
import { useTranslation } from 'react-i18next';
import ImageActionMenu from '../../common/ImageActionMenu';

interface MenuSettingsProps {
  config: any;
  defaultConfig: any;
  mainMenuCategories: any[];
  subMenuCategories: any[];
  isMenuForcedDisabled?: boolean;
  onMenuSettingChange: (setting: any, value: any) => void;
  onHeaderSettingChange: (setting: any, value: any) => void;
  onOpenMenuLogoLibrary: () => void;
  onRemoveMenuLogo: () => void;
  onOpenCategoryImageLibrary: (categoryId: string, imageType: 'category' | 'banner') => void;
  onUpdateCategoryImage: (categoryId: string, imageType: 'category' | 'banner') => void;
  onViewCategoryImage: (categoryId: string, imageType: 'category' | 'banner') => void;
  onRemoveCategoryImage: (categoryId: string, imageType: 'category' | 'banner') => void;
  onToggleCategoryVisibility: (categoryId: string, isVisible: boolean) => void;
  onMoveCategoryUp: (categoryId: string, group: 'main' | 'sub') => void;
  onMoveCategoryDown: (categoryId: string, group: 'main' | 'sub') => void;
}

const MenuSettings: React.FC<MenuSettingsProps> = ({
  config,
  defaultConfig,
  mainMenuCategories,
  subMenuCategories,
  isMenuForcedDisabled = false,
  onMenuSettingChange,
  onHeaderSettingChange,
  onOpenMenuLogoLibrary,
  onRemoveMenuLogo,
  onOpenCategoryImageLibrary,
  onUpdateCategoryImage,
  onViewCategoryImage,
  onRemoveCategoryImage,
  onToggleCategoryVisibility,
  onMoveCategoryUp,
  onMoveCategoryDown
}) => {
  const { t } = useTranslation('salesSystem');

  // Kategori/banner resmi işlem menüsü (Göster / Güncelle / Kaldır)
  const [imageMenuAnchor, setImageMenuAnchor] = useState<HTMLElement | null>(null);
  const [imageMenuTarget, setImageMenuTarget] = useState<{ categoryId: string; imageType: 'category' | 'banner' } | null>(null);

  const closeImageMenu = () => {
    setImageMenuAnchor(null);
    setImageMenuTarget(null);
  };

  // Resim ikonuna tıklandığında: resim varsa menü aç, yoksa doğrudan kütüphaneyi aç
  const handleImageIconClick = (
    event: React.MouseEvent<HTMLElement>,
    categoryId: string,
    imageType: 'category' | 'banner',
    hasImage: boolean
  ) => {
    if (hasImage) {
      setImageMenuTarget({ categoryId, imageType });
      setImageMenuAnchor(event.currentTarget);
    } else {
      onOpenCategoryImageLibrary(categoryId, imageType);
    }
  };
  const [expandedParents, setExpandedParents] = useState<Record<string, boolean>>({});
  const categoryActionsDisabled = false;
  const orderedMainCategories = useMemo(
    () => [...mainMenuCategories].sort((a: any, b: any) => Number(a.displayOrder ?? 0) - Number(b.displayOrder ?? 0)),
    [mainMenuCategories]
  );
  const orderedSubCategories = useMemo(
    () => [...subMenuCategories].sort((a: any, b: any) => Number(a.displayOrder ?? 0) - Number(b.displayOrder ?? 0)),
    [subMenuCategories]
  );
  const subCategoriesByRootParent = useMemo(() => {
    const mainIds = new Set(orderedMainCategories.map((c: any) => String(c.categoryId ?? '').toLowerCase()));
    const allCats = [...orderedMainCategories, ...orderedSubCategories];
    const parentLookup = new Map<string, string>();
    allCats.forEach((c: any) => {
      const id = String(c.categoryId ?? '').toLowerCase();
      const parentId = String(c.parentId ?? '').toLowerCase();
      if (parentId && parentId !== 'null' && parentId !== 'undefined' && parentId !== '00000000-0000-0000-0000-000000000000') {
        parentLookup.set(id, parentId);
      }
    });

    const findRoot = (catId: string, visited = new Set<string>()): string => {
      if (mainIds.has(catId)) return catId;
      if (visited.has(catId)) return catId;
      visited.add(catId);
      const parent = parentLookup.get(catId);
      if (!parent) return catId;
      return findRoot(parent, visited);
    };

    const map = new Map<string, any[]>();
    orderedSubCategories.forEach((category: any) => {
      const catId = String(category.categoryId ?? '').toLowerCase();
      const rootId = findRoot(catId);
      if (!map.has(rootId)) map.set(rootId, []);
      map.get(rootId)!.push(category);
    });
    return map;
  }, [orderedMainCategories, orderedSubCategories]);
  const toggleParentExpand = (categoryId: string) => {
    setExpandedParents((prev) => ({ ...prev, [categoryId]: !prev[categoryId] }));
  };
  const isHeaderEnabled = config.headerSettings?.enabled !== false;
  const menuLogoControlsDisabled = !config.menuSettings.enabled || isMenuForcedDisabled || isHeaderEnabled;
  const effectiveMenuLogoUrl = config.headerSettings?.logoUrl || null;

  const renderCategoryRow = (
    category: any,
    index: number,
    group: 'main' | 'sub',
    total: number,
    isChild: boolean,
    hasChildren: boolean,
    isExpanded: boolean
  ) => (
    <ListItem
      key={`${group}-${category.categoryId}`}
      sx={{
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        mb: 1,
        backgroundColor: category.isVisible ? 'background.paper' : 'action.hover',
        ml: isChild ? 4 : 0
      }}
    >
      <ListItemIcon>
        <Box sx={{ width: 28, display: 'flex', justifyContent: 'center', mr: 1 }}>
          {hasChildren ? (
            <IconButton size="small" onClick={() => toggleParentExpand(category.categoryId)}>
              {isExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
            </IconButton>
          ) : null}
        </Box>
        {category.categoryImageUrl ? (
          <Box
            component="img"
            src={category.categoryImageUrl}
            alt={category.categoryName}
            sx={{
              width: 40,
              height: 40,
              objectFit: 'cover',
              borderRadius: 1,
              border: '1px solid #e0e0e0'
            }}
          />
        ) : (
          <CategoryIcon sx={{ fontSize: 40, color: 'action.active' }} />
        )}
      </ListItemIcon>
      <ListItemText
        primary={category.categoryName}
        secondary={t('ecommerceMenuSettings.orderLabel', { order: index + 1 })}
      />
      <ListItemSecondaryAction>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Tooltip title={t('ecommerceMenuSettings.actions.selectCategoryImage')}>
            <IconButton
              size="small"
              onClick={(e) => handleImageIconClick(e, category.categoryId, 'category', Boolean(category.categoryImageUrl))}
              color={category.categoryImageUrl ? 'primary' : 'default'}
              disabled={categoryActionsDisabled}
            >
              <ImageIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('ecommerceMenuSettings.actions.selectBannerImage')}>
            <IconButton
              size="small"
              onClick={(e) => handleImageIconClick(e, category.categoryId, 'banner', Boolean(category.bannerImageUrl))}
              color={category.bannerImageUrl ? 'primary' : 'default'}
              disabled={categoryActionsDisabled}
            >
              <PhotoLibraryIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={category.isVisible ? t('ecommerceMenuSettings.actions.hide') : t('ecommerceMenuSettings.actions.show')}>
            <IconButton
              size="small"
              onClick={() => onToggleCategoryVisibility(category.categoryId, !category.isVisible)}
              disabled={categoryActionsDisabled}
            >
              {category.isVisible ? <VisibilityIcon /> : <VisibilityOffIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title={t('ecommerceMenuSettings.actions.moveUp')}>
            <span>
              <IconButton
                size="small"
                onClick={() => onMoveCategoryUp(category.categoryId, group)}
                disabled={index === 0 || categoryActionsDisabled}
              >
                <KeyboardArrowUpIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('ecommerceMenuSettings.actions.moveDown')}>
            <span>
              <IconButton
                size="small"
                onClick={() => onMoveCategoryDown(category.categoryId, group)}
                disabled={index === total - 1 || categoryActionsDisabled}
              >
                <KeyboardArrowDownIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </ListItemSecondaryAction>
    </ListItem>
  );

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
        {t('ecommerceMenuSettings.title')}
      </Typography>

      {isMenuForcedDisabled && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('ecommerceMenuSettings.header5Warning')}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <FormLabel component="legend">{t('ecommerceMenuSettings.menuStyle')}</FormLabel>
            <RadioGroup
              row
              value={config.menuSettings.menuType}
              onChange={(e) => onMenuSettingChange('menuType', e.target.value)}
            >
              <FormControlLabel value="menu1" control={<Radio />} label={t('ecommerceMenuSettings.menuType.menu1')} />
              <FormControlLabel value="menu2" control={<Radio />} label={t('ecommerceMenuSettings.menuType.menu2')} />
              <FormControlLabel value="menu3" control={<Radio />} label={t('ecommerceMenuSettings.menuType.menu3')} />
            </RadioGroup>
          </FormControl>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            {t('ecommerceMenuSettings.menuActivePassive')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.menuSettings.enabled}
                    onChange={(e) => onMenuSettingChange('enabled', e.target.checked)}
                    disabled={isMenuForcedDisabled}
                  />
                }
                label={t('ecommerceMenuSettings.menuActive')}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            {t('ecommerceMenuSettings.menuLeftLogo')}
          </Typography>
          {isHeaderEnabled && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('ecommerceMenuSettings.menuLeftLogoLocked')}
            </Alert>
          )}
          <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Mevcut Logo
                </Typography>
                {effectiveMenuLogoUrl ? (
                  <Box
                    component="img"
                    src={effectiveMenuLogoUrl}
                    alt="Menü Sol Logo Önizleme"
                    sx={{
                      height: 'auto',
                      width: config.headerSettings.logoWidth || 120,
                      maxWidth: '100%',
                      objectFit: 'contain',
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      p: 0.5,
                      mx: 'auto'
                    }}
                  />
                ) : (
                  <Box sx={{ 
                    width: 80, 
                    height: 80, 
                    bgcolor: 'grey.100', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    borderRadius: 1,
                    border: '1px solid #e0e0e0',
                    mx: 'auto'
                  }}>
                    <StoreIcon sx={{ color: 'grey.500' }} />
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} md={8}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.menuSettings.showMenuLeftLogo === true}
                      onChange={(e) => onMenuSettingChange('showMenuLeftLogo', e.target.checked)}
                      disabled={menuLogoControlsDisabled}
                    />
                  }
                  label={t('ecommerceMenuSettings.showMenuLeftLogo')}
                />
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('ecommerceMenuSettings.actions.selectLogo')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      onClick={onOpenMenuLogoLibrary}
                      startIcon={<PhotoLibraryIcon />}
                      disabled={menuLogoControlsDisabled}
                    >
                      {t('ecommerceMenuSettings.actions.selectLogo')}
                    </Button>
                    {effectiveMenuLogoUrl && (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={onRemoveMenuLogo}
                        startIcon={<RestartAltIcon />}
                        disabled={menuLogoControlsDisabled}
                      >
                        {t('ecommerceMenuSettings.actions.remove')}
                      </Button>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Resim kütüphanesinden logonuzu seçin. Kütüphanede resim yoksa önce "Resim Kütüphanesi" sayfasından resim yükleyin.
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Logo Boyutu Ayarı (Genişlik)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1 }}>
                    <Slider
                      value={config.headerSettings.logoWidth || 120}
                      onChange={(e, newValue) => onHeaderSettingChange('logoWidth', newValue as number)}
                      aria-labelledby="menu-logo-size-slider"
                      valueLabelDisplay="auto"
                      step={10}
                      min={50}
                      max={500}
                      disabled={menuLogoControlsDisabled || config.menuSettings.showMenuLeftLogo !== true}
                    />
                    <Typography variant="body1" sx={{ minWidth: '40px' }}>
                      {config.headerSettings.logoWidth || 120}px
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, px: 1 }}>
                    Menü solunda görünecek logonun genişliğini ayarlayın.
                  </Typography>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Logo Boyutu Ayarı (Yükseklik)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1 }}>
                    <Slider
                      value={config.headerSettings.logoHeight || 50}
                      onChange={(e, newValue) => onHeaderSettingChange('logoHeight', newValue as number)}
                      aria-labelledby="menu-logo-height-slider"
                      valueLabelDisplay="auto"
                      step={5}
                      min={20}
                      max={200}
                      disabled={menuLogoControlsDisabled || config.menuSettings.showMenuLeftLogo !== true}
                    />
                    <Typography variant="body1" sx={{ minWidth: '40px' }}>
                      {config.headerSettings.logoHeight || 50}px
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
          
          {/* Menü 3 Özel Ayarları */}
          {config.menuSettings.menuType === 'menu3' && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('ecommerceMenuSettings.menu3SpecialSettings')}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.menuSettings.showAllCategories}
                          onChange={(e) => onMenuSettingChange('showAllCategories', e.target.checked)}
                          disabled={!config.menuSettings.enabled}
                        />
                      }
                      label={t('ecommerceMenuSettings.showAllCategoriesButton')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.menuSettings.showHomePage}
                          onChange={(e) => onMenuSettingChange('showHomePage', e.target.checked)}
                          disabled={!config.menuSettings.enabled}
                        />
                      }
                      label={t('ecommerceMenuSettings.showHomeButton')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.menuSettings.showQuoteButton === true}
                          onChange={(e) => onMenuSettingChange('showQuoteButton', e.target.checked)}
                          disabled={!config.menuSettings.enabled}
                        />
                      }
                      label={t('ecommerceMenuSettings.showQuoteButton')}
                    />
                  </Grid>
                  {config.menuSettings.showQuoteButton === true && (
                    <>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t('ecommerceMenuSettings.quoteButtonTitle')}
                          value={config.menuSettings.quoteButtonTitle || ''}
                          onChange={(e) => onMenuSettingChange('quoteButtonTitle', e.target.value)}
                          disabled={!config.menuSettings.enabled}
                        />
                      </Grid>
                      <Grid item xs={12} sm={8}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t('ecommerceMenuSettings.quoteButtonSubtitle')}
                          value={config.menuSettings.quoteButtonSubtitle || ''}
                          onChange={(e) => onMenuSettingChange('quoteButtonSubtitle', e.target.value)}
                          disabled={!config.menuSettings.enabled}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t('ecommerceMenuSettings.quoteButtonLink')}
                          value={config.menuSettings.quoteButtonLink || ''}
                          onChange={(e) => onMenuSettingChange('quoteButtonLink', e.target.value)}
                          disabled={!config.menuSettings.enabled}
                          placeholder="teklif-formu"
                          helperText={t('ecommerceMenuSettings.quoteButtonLinkHelp')}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Paper>
            </>
          )}
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            {t('ecommerceMenuSettings.sizingSettings')}
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography id="menu-vertical-padding-slider" gutterBottom>
                {t('ecommerceMenuSettings.verticalPadding')}
              </Typography>
              <Button
                size="small"
                startIcon={<RestartAltIcon />}
                onClick={() => {
                  onMenuSettingChange('verticalPadding', defaultConfig.menuSettings.verticalPadding);
                }}
                disabled={!config.menuSettings.enabled}
              >
                {t('ecommerceMenuSettings.actions.reset')}
              </Button>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1 }}>
              <Slider
                value={config.menuSettings.verticalPadding || 12}
                onChange={(e, newValue) => onMenuSettingChange('verticalPadding', newValue as number)}
                aria-labelledby="menu-vertical-padding-slider"
                valueLabelDisplay="auto"
                step={2}
                min={4}
                max={32}
                disabled={!config.menuSettings.enabled}
              />
              <Typography variant="body1" sx={{ minWidth: '40px' }}>
                {config.menuSettings.verticalPadding || 12}px
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, px: 1 }}>
              {t('ecommerceMenuSettings.verticalPaddingHelp')}
            </Typography>
          </Paper>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            {t('ecommerceMenuSettings.colorSettings')}
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {t('ecommerceMenuSettings.menuColors')}
              </Typography>
              <Button
                size="small"
                startIcon={<RestartAltIcon />}
                onClick={() => {
                  onMenuSettingChange('backgroundColor', defaultConfig.menuSettings.backgroundColor);
                  onMenuSettingChange('textColor', defaultConfig.menuSettings.textColor);
                }}
                disabled={!config.menuSettings.enabled}
              >
                {t('ecommerceMenuSettings.actions.reset')}
              </Button>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('ecommerceMenuSettings.backgroundColor')}
                  value={config.menuSettings.backgroundColor}
                  onChange={(e) => onMenuSettingChange('backgroundColor', e.target.value)}
                  variant="outlined"
                  size="small"
                  disabled={!config.menuSettings.enabled}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          component="input"
                          type="color"
                          value={config.menuSettings.backgroundColor}
                          onChange={(e: any) => onMenuSettingChange('backgroundColor', e.target.value)}
                          disabled={!config.menuSettings.enabled}
                          sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                        />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('ecommerceMenuSettings.textColor')}
                  value={config.menuSettings.textColor}
                  onChange={(e) => onMenuSettingChange('textColor', e.target.value)}
                  variant="outlined"
                  size="small"
                  disabled={!config.menuSettings.enabled}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          component="input"
                          type="color"
                          value={config.menuSettings.textColor}
                          onChange={(e: any) => onMenuSettingChange('textColor', e.target.value)}
                          disabled={!config.menuSettings.enabled}
                          sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                        />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
            </Grid>
          </Paper>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            {t('ecommerceMenuSettings.menuPosition')}
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontSize: '0.9rem', mb: 1, fontWeight: 'normal' }}>
                {t('ecommerceMenuSettings.menuAlignment')}
              </FormLabel>
              <RadioGroup
                name="menuPosition"
                value={config.menuSettings.menuPosition || 'center'}
                onChange={(e) => onMenuSettingChange('menuPosition', e.target.value)}
              >
                <FormControlLabel 
                  value="left" 
                  control={<Radio />} 
                  label={t('ecommerceMenuSettings.align.left')}
                  disabled={!config.menuSettings.enabled}
                />
                <FormControlLabel 
                  value="center" 
                  control={<Radio />} 
                  label={t('ecommerceMenuSettings.align.center')}
                  disabled={!config.menuSettings.enabled}
                />
                <FormControlLabel 
                  value="right" 
                  control={<Radio />} 
                  label={t('ecommerceMenuSettings.align.right')}
                  disabled={!config.menuSettings.enabled}
                />
              </RadioGroup>
            </FormControl>
          </Paper>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            {t('ecommerceMenuSettings.menuTextStyle')}
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontSize: '0.9rem', mb: 1, fontWeight: 'normal' }}>
                {t('ecommerceMenuSettings.textTransform')}
              </FormLabel>
              <RadioGroup
                name="textTransform"
                value={config.menuSettings.textTransform || 'none'}
                onChange={(e) => onMenuSettingChange('textTransform', e.target.value)}
              >
                <FormControlLabel 
                  value="none" 
                  control={<Radio />} 
                  label={t('ecommerceMenuSettings.textTransforms.none')}
                  disabled={!config.menuSettings.enabled}
                />
                <FormControlLabel 
                  value="uppercase" 
                  control={<Radio />} 
                  label={t('ecommerceMenuSettings.textTransforms.uppercase')}
                  disabled={!config.menuSettings.enabled}
                />
                <FormControlLabel 
                  value="lowercase" 
                  control={<Radio />} 
                  label={t('ecommerceMenuSettings.textTransforms.lowercase')}
                  disabled={!config.menuSettings.enabled}
                />
                <FormControlLabel 
                  value="capitalize" 
                  control={<Radio />} 
                  label={t('ecommerceMenuSettings.textTransforms.capitalize')}
                  disabled={!config.menuSettings.enabled}
                />
              </RadioGroup>
            </FormControl>
          </Paper>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            {t('ecommerceMenuSettings.menuCategories')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('ecommerceMenuSettings.menuCategoriesHelp')}
          </Typography>

          {mainMenuCategories.length === 0 && subMenuCategories.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('ecommerceMenuSettings.noEcommerceCategory')}
            </Alert>
          ) : (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1, mb: 1 }}>
                {t('ecommerceMenuSettings.mainCategories')}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                {mainMenuCategories.length === 0 ? (
                  <Alert severity="warning">{t('ecommerceMenuSettings.mainCategoryNotFound')}</Alert>
                ) : (
                  <List>
                    {orderedMainCategories.map((category: any, index: number) => {
                      const catKey = String(category.categoryId ?? '').toLowerCase();
                      const children = subCategoriesByRootParent.get(catKey) || [];
                      const isExpanded = !!expandedParents[category.categoryId];
                      return (
                        <React.Fragment key={`main-group-${category.categoryId}`}>
                          {renderCategoryRow(
                            category,
                            index,
                            'main',
                            orderedMainCategories.length,
                            false,
                            children.length > 0,
                            isExpanded
                          )}
                          {isExpanded &&
                            children.map((subCategory: any, subIndex: number) =>
                              renderCategoryRow(
                                subCategory,
                                subIndex,
                                'sub',
                                children.length,
                                true,
                                false,
                                false
                              )
                            )}
                        </React.Fragment>
                      );
                    })}
                  </List>
                )}
              </Paper>
            </>
          )}
        </Grid>
      </Grid>

      {/* Kategori/Banner resmi işlem menüsü */}
      <ImageActionMenu
        anchorEl={imageMenuAnchor}
        open={Boolean(imageMenuAnchor)}
        onClose={closeImageMenu}
        onView={() => imageMenuTarget && onViewCategoryImage(imageMenuTarget.categoryId, imageMenuTarget.imageType)}
        onUpdate={() => imageMenuTarget && onUpdateCategoryImage(imageMenuTarget.categoryId, imageMenuTarget.imageType)}
        onRemove={() => imageMenuTarget && onRemoveCategoryImage(imageMenuTarget.categoryId, imageMenuTarget.imageType)}
        labels={{
          view: t('ecommerceMenuSettings.actions.viewImage'),
          update: t('ecommerceMenuSettings.actions.updateImage'),
          remove: t('ecommerceMenuSettings.actions.removeImage'),
        }}
      />
    </Paper>
  );
};

export default MenuSettings;

